import { type ReactElement, type ReactNode } from 'react';
import { UiIcon } from './icons';

/**
 * D550 (ADMIN-A): the Claude Design A08 "Colunas" menu body and the vertical activity timeline of the record drawer — the two
 * pieces `DirectoryScreen` drew by hand. Both render inside the design's menu / drawer surfaces and use only theme classes.
 */

// ---- ColumnPicker ----------------------------------------------------------------------------------------------------------------

export interface ColumnPickerItem {
  readonly id: string;
  readonly label: string;
  /** Shown in the list. */
  readonly on: boolean;
  /** The first column: always on, cannot move (a lock instead of the arrows). */
  readonly locked?: boolean;
  readonly canMoveUp: boolean;
  readonly canMoveDown: boolean;
}

export interface ColumnPickerProps {
  readonly title: string;
  readonly resetLabel: string;
  readonly moveUpLabel: string;
  readonly moveDownLabel: string;
  /** The foot note under the rows. */
  readonly note: ReactNode;
  readonly items: readonly ColumnPickerItem[];
  readonly onToggle: (id: string) => void;
  readonly onMove: (id: string, direction: -1 | 1) => void;
  readonly onReset: () => void;
}

/**
 * The column picker menu body (A08): the heading with a reset link, then one row per column — a checkbox, the name, the reorder
 * arrows (a lock for the pinned first column) — and the note. Every control keeps its 48px box; the 18px square and the 26px
 * arrows are drawn inside it. Put it inside a `Popover`.
 */
export function ColumnPicker({ title, resetLabel, moveUpLabel, moveDownLabel, note, items, onToggle, onMove, onReset }: ColumnPickerProps): ReactElement {
  return (
    <>
      <div className="vello-admin-a-picker__head">
        <span className="vello-menu__heading vello-admin-a-picker__title">{title}</span>
        <span className="vello-grow" />
        <button type="button" className="vello-menu__link" onClick={onReset}>
          {resetLabel}
        </button>
      </div>
      {items.map((item) => (
        <div key={item.id} className="vello-admin-a-picker__row">
          <button type="button" className="vello-checkbtn" aria-pressed={item.on} aria-label={item.label} disabled={item.locked === true} onClick={() => onToggle(item.id)}>
            <span className="vello-checkbox" data-on={item.on}>
              <UiIcon name="check" size={12} />
            </span>
          </button>
          <span className="vello-admin-a-picker__label vello-grow" data-on={item.on}>
            {item.label}
          </span>
          {item.locked === true ? (
            <span className="vello-admin-a-picker__lock">
              <UiIcon name="lock" size={13} />
            </span>
          ) : (
            <>
              <button type="button" className="vello-arrowbtn" aria-label={moveUpLabel} disabled={!item.canMoveUp} onClick={() => onMove(item.id, -1)}>
                <UiIcon name="chevronUp" size={14} />
              </button>
              <button type="button" className="vello-arrowbtn" aria-label={moveDownLabel} disabled={!item.canMoveDown} onClick={() => onMove(item.id, 1)}>
                <UiIcon name="chevronDown" size={14} />
              </button>
            </>
          )}
        </div>
      ))}
      <div className="vello-menu__note">{note}</div>
    </>
  );
}

// ---- Timeline --------------------------------------------------------------------------------------------------------------------

export interface TimelineEntry {
  readonly id: string;
  /** The dot: neutral (ink), warning or danger. */
  readonly tone: 'neutral' | 'warning' | 'danger';
  readonly title: ReactNode;
  /** A line under the title (a reason). */
  readonly detail?: ReactNode;
  /** The mono line under it (the time and the actor). */
  readonly meta?: ReactNode;
}

/** A vertical timeline: a dot and a hairline rail on the left, the title, the reason and the mono time/actor line on the right. */
export function Timeline({ entries }: { entries: readonly TimelineEntry[] }): ReactElement {
  return (
    <ol className="vello-admin-a-timeline">
      {entries.map((entry) => (
        <li key={entry.id} className="vello-admin-a-timeline__item" data-tone={entry.tone === 'neutral' ? undefined : entry.tone}>
          <span className="vello-admin-a-timeline__rail" aria-hidden="true">
            <span className="vello-admin-a-timeline__dot" />
            <span className="vello-admin-a-timeline__line" />
          </span>
          <div className="vello-admin-a-timeline__body">
            <div className="vello-admin-a-timeline__title">{entry.title}</div>
            {entry.detail !== undefined && <div className="vello-admin-a-timeline__detail">{entry.detail}</div>}
            {entry.meta !== undefined && <div className="vello-admin-a-timeline__meta">{entry.meta}</div>}
          </div>
        </li>
      ))}
    </ol>
  );
}
